/**
 * ChatPanel — in-flow chat panel with tab bar + drag-resize handle
 * (issue #351, subtask 09).
 *
 * Renders the registered chat tabs (see ChatTabRegistry). v1 ships
 * one tab (Techsupport, registered by ChatProvider). #761 will add
 * agentic-AI tabs via the same registry — no panel-side changes.
 *
 * The panel is a flex sibling of the main content area inside
 * ``ChatProvider`` so opening the panel shrinks the main column
 * rather than overlaying it (WhatsApp-Web behaviour). The
 * ``--chat-panel-width`` document-root variable is published by
 * ChatProvider so the design-system MainLayout header also tracks
 * the panel width.
 *
 * Width is user-resizable via the left-edge drag handle.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import type { ChatTabDescriptor } from './ChatTabRegistry';
import './ChatPanel.css';

export interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  tabs: ChatTabDescriptor[];
  width: number;
  onWidthChange: (next: number) => void;
}

const MIN_WIDTH = 320;
const MAX_WIDTH_VW_FRACTION = 0.8;

export const ChatPanel: React.FC<ChatPanelProps> = ({
  isOpen,
  onClose,
  tabs,
  width,
  onWidthChange,
}) => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(tabs[0]?.id ?? null);

  const active = tabs.find((tab) => tab.id === activeId) ?? tabs[0] ?? null;

  // Drag-resize state. ``startXRef`` captures the mouse position +
  // panel width at mousedown so we can derive the new width as the
  // user drags. We attach the move/up handlers to ``window`` so the
  // drag continues even if the cursor leaves the handle's hitbox.
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const stopDrag = useCallback(() => {
    dragRef.current = null;
    document.body.style.removeProperty('cursor');
    document.body.style.removeProperty('user-select');
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      // Drag handle is on the LEFT edge of the panel — moving the
      // cursor left grows the panel (delta > 0), right shrinks it.
      const delta = dragRef.current.startX - e.clientX;
      const next = dragRef.current.startWidth + delta;
      const maxWidth = Math.floor(window.innerWidth * MAX_WIDTH_VW_FRACTION);
      const clamped = Math.max(MIN_WIDTH, Math.min(maxWidth, next));
      onWidthChange(clamped);
    };
    const onUp = () => stopDrag();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [onWidthChange, stopDrag]);

  const onHandleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startWidth: width };
      // Keep the resize cursor + suppress text-selection across the
      // whole document during the drag.
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [width],
  );

  if (!isOpen) return null;

  return (
    <aside
      className="chat-panel chat-panel--push"
      role="complementary"
      aria-label={t('chat.panel.title', 'Chat')}
      style={{ flexBasis: width, width }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t('chat.panel.resizeHandle', 'Resize chat panel')}
        className="chat-panel__resize-handle"
        onMouseDown={onHandleMouseDown}
      />
      <div className="chat-panel__tabs-row">
        {tabs.length > 0 && (
          <div role="tablist" aria-label={t('chat.panel.tabsLabel', 'Chat tabs')} className="chat-panel__tabs">
            {tabs.map((tab) => {
              const isActive = active?.id === tab.id;
              const badge = tab.badgeCount && tab.badgeCount > 0 ? tab.badgeCount : null;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  type="button"
                  aria-selected={isActive}
                  className={`chat-panel__tab${isActive ? ' chat-panel__tab--active' : ''}`}
                  onClick={() => setActiveId(tab.id)}
                >
                  <span>{tab.label}</span>
                  {badge !== null && <span className="chat-panel__tab-badge">{badge > 99 ? '99+' : badge}</span>}
                </button>
              );
            })}
          </div>
        )}
        <button
          type="button"
          className="chat-panel__close"
          aria-label={t('common.close', 'Close')}
          onClick={onClose}
        >
          <X size={20} aria-hidden />
        </button>
      </div>
      <div className="chat-panel__body" role="tabpanel">
        {active ? active.render() : <p className="chat-panel__empty">{t('chat.panel.empty', 'No chat tabs registered')}</p>}
      </div>
    </aside>
  );
};
